import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BaseHttpResponseError, HttpMethods, UserNotFoundError } from '@cbre/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { IOption } from '@shared/models/icommons.model';
import { ISearchFilterSettings } from '@shared/models/isearch-filter-settings.model';
import { IUserFilters, IUserSettings, IUserViewsSettings } from '@shared/models/iuser-settings.model';
import { ILocationPermission, IUser } from '@shared/models/iuser.model';
import { LanguageLocale } from '@shared/models/language.model';
import { AppStorageService, LOCALE_ID } from '@shared/services/app-storage.service';
import { UserSharedService } from '@shared/services/user-shared.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserService extends HttpMethods {
  constructor(
    http: HttpClient,
    private sharedService: UserSharedService,
    @Inject(CbreLoggerToken) private logger: CbreLogger,
    private storageService: AppStorageService
  ) {
    super(http);
  }

  public getUsers(userId): Observable<IUser> {
    const uri = `/users/${userId}`;
    return this.httpGet(uri);
  }

  public getUserPermissions(userId): Observable<ILocationPermission[]> {
    const uri = `/users/${userId}/perms/location`;
    return this.httpGet(uri);
  }

  public getSearchFiltersCatalog(query: string): Observable<ISearchFilterSettings[]> {
    const uri = `/lookup/filters`;
    const params = new HttpParams({ fromObject: { query } });

    return this.httpGet(uri, params);
  }

  // TODO: Delete method when filters?query functionality is done (backend)
  /**
   * Bring all the filters catalog in a single object.
   */
  public getSearchFiltersTemp(): Observable<IUserFilters> {
    const uri = `/lookup/filters`;
    return this.httpGet(uri);
  }

  public getViewSettingsCatalog(): Observable<IUserViewsSettings> {
    const uri = `/lookup/viewopts`;
    return this.httpGet(uri);
  }

  public getViewSettingsLocales(): Observable<IOption[]> {
    const uri = `/lookup/settings/locales`;
    return this.httpGet(uri);
  }

  public getUserGeoLabels(): Observable<any> {
    return this.httpGet(`/lookup/geoaliases`);
  }

  public getUserSettings(userId: number): Observable<IUserSettings> {
    return this.sharedService.getUserSettings(userId);
  }

  public postUserSettings(userId: number, data: Partial<IUserSettings>): Observable<any> {
    const uri = `/users/${userId}/settings`;
    return this.httpPut(uri, data).pipe(tap(() => this.sharedService.clearUserSettingsCache(userId)));
  }

  public markets(userId: number) {
    return this.sharedService.markets(userId);
  }

  public setDefaultLang(userId: number, localeSkey: number): Observable<any> {
    this.storageService.setItem(LOCALE_ID, localeSkey);
    return this.httpPut(`/users/${userId}/defaultlang?localeId=${localeSkey}`).pipe(
      map(defaultlan => (!!defaultlan ? defaultlan : null))
    );
  }

  public getUserLanguages(): Observable<LanguageLocale[]> {
    const uri = '/users/userlanguages';
    return this.httpGet(uri).pipe(
      catchError(error => {
        // log error
        this.logger.error(this._getErrorMessage(error));
        // return empty if error is user not found
        // (this means sso passed and we need to provide at least the default or continue the flow)
        if (error instanceof UserNotFoundError) return of([]);
        // re-throw for any other case
        return throwError(() => error);
      })
    );
  }

  private _getErrorMessage(error: HttpErrorResponse): string | HttpErrorResponse {
    return error instanceof BaseHttpResponseError ? error.description : error;
  }

  public getDefaultLang(userId: number): Observable<IOption> {
    return this.httpGet(`/users/${userId}/defaultlang`).pipe(map(defaultLan => (!!defaultLan ? defaultLan : null)));
  }
}
